import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BookOpen, Loader2, Coins, Copy, Wand2, CalendarClock } from "lucide-react";
import ScheduleDialog from "@/components/ScheduleDialog";

export default function AIBlog() {
  const { isOwner, refreshBalance } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [topic, setTopic] = useState("");
  const [niche, setNiche] = useState("real estate");
  const [tone, setTone] = useState("informative");
  const [language, setLanguage] = useState("en");
  const [length, setLength] = useState("medium");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  useEffect(() => { api.get("/ai/pricing").then((r) => setPricing(r.data)); }, []);

  const run = async () => {
    if (!topic.trim()) return toast.error("Voeg een onderwerp toe");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/blog", { topic, niche, tone, language, length });
      setResult(data);
      toast.success("Blogartikel gegenereerd");
      refreshBalance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Genereren mislukt");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text, label = "Gekopieerd") => {
    await navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const copyAll = async () => {
    if (!result) return;
    const md = [
      `# ${result.title || ""}`,
      "",
      result.meta_description ? `> ${result.meta_description}\n` : "",
      result.intro || "",
      "",
      ...(Array.isArray(result.sections) ? result.sections.map((s) => `## ${s.heading}\n\n${s.body}\n`) : []),
      result.conclusion || "",
      "",
      Array.isArray(result.tags) ? `Tags: ${result.tags.join(", ")}` : "",
    ].join("\n");
    await navigator.clipboard.writeText(md);
    toast.success("Volledig artikel gekopieerd (Markdown)");
  };

  return (
    <div className="p-6 md:p-10 lg:p-14 max-w-[1400px]" data-testid="ai-blog-page">
      <div className="mb-10">
        <div className="font-mono-tech text-[10px] text-amber-400 mb-3">AI · BLOG · STUDIO</div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-light leading-none">
          SEO-blogs op <span className="font-bold text-amber-400">autopilot.</span>
        </h1>
        <p className="text-zinc-400 mt-4 max-w-2xl">
          Genereer complete blogartikelen met titel, meta-description, secties en tags — klaar om te publiceren.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Input */}
        <div className="lg:col-span-5 space-y-4">
          <div className="bg-[#121214] border border-white/5 rounded-lg p-5 space-y-4">
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">ONDERWERP *</Label>
              <Textarea
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Bijvoorbeeld: '5 tips om je huis snel te verkopen in 2026'"
                rows={3}
                data-testid="blog-topic"
                className="mt-2 bg-[#0b0b0d] border-white/10"
              />
            </div>
            <div>
              <Label className="font-mono-tech text-[10px] text-zinc-500">NICHE / DOELGROEP</Label>
              <Input
                value={niche}
                onChange={(e) => setNiche(e.target.value)}
                placeholder="real estate, tech, fitness, marketing…"
                data-testid="blog-niche"
                className="mt-2 bg-[#0b0b0d] border-white/10 h-11"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">TOON</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger data-testid="blog-tone" className="mt-2 bg-[#0b0b0d] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/10">
                    <SelectItem value="informative">Informatief</SelectItem>
                    <SelectItem value="expert">Expert</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                    <SelectItem value="persuasive">Overtuigend</SelectItem>
                    <SelectItem value="luxury">Luxe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">TAAL</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger data-testid="blog-lang" className="mt-2 bg-[#0b0b0d] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/10">
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="nl">Nederlands</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="de">Deutsch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-mono-tech text-[10px] text-zinc-500">LENGTE</Label>
                <Select value={length} onValueChange={setLength}>
                  <SelectTrigger data-testid="blog-length" className="mt-2 bg-[#0b0b0d] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1e] border-white/10">
                    <SelectItem value="short">Kort · 300-500w</SelectItem>
                    <SelectItem value="medium">Medium · 700-900w</SelectItem>
                    <SelectItem value="long">Lang · 1200-1500w</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <Button
            onClick={run}
            disabled={busy || !topic.trim()}
            data-testid="blog-generate"
            className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" strokeWidth={1.5} />}
            Genereer blogartikel
            {!isOwner && pricing && (
              <span className="ml-2 text-[10px] font-mono-tech opacity-75 flex items-center gap-1">
                <Coins className="w-3 h-3" /> {pricing.blog_credits}
              </span>
            )}
          </Button>
        </div>

        {/* Output */}
        <div className="lg:col-span-7">
          {result ? (
            <div className="bg-[#121214] border border-white/5 rounded-lg p-6 space-y-5" data-testid="blog-result">
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setScheduleOpen(true)}
                  data-testid="blog-schedule"
                  className="text-xs font-mono-tech text-amber-400 hover:text-amber-300 flex items-center gap-1.5"
                >
                  <CalendarClock className="w-3.5 h-3.5" strokeWidth={1.5} /> Plan in kalender
                </button>
                <button
                  onClick={copyAll}
                  data-testid="blog-copy-all"
                  className="text-xs font-mono-tech text-amber-400 hover:text-amber-300 flex items-center gap-1.5"
                >
                  <Copy className="w-3.5 h-3.5" strokeWidth={1.5} /> Kopieer alles (MD)
                </button>
              </div>
              {result.title && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="font-mono-tech text-[10px] text-zinc-500">TITEL</Label>
                    <button onClick={() => copy(result.title, "Titel gekopieerd")}
                      className="text-zinc-500 hover:text-amber-400"><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="font-heading text-2xl font-bold tracking-tight">{result.title}</div>
                </div>
              )}
              {result.slug && (
                <div className="font-mono-tech text-[10px] text-zinc-500">
                  SLUG · <span className="text-zinc-300">{result.slug}</span>
                </div>
              )}
              {result.meta_description && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label className="font-mono-tech text-[10px] text-zinc-500">META DESCRIPTION</Label>
                    <button onClick={() => copy(result.meta_description, "Meta gekopieerd")}
                      className="text-zinc-500 hover:text-amber-400"><Copy className="w-3.5 h-3.5" /></button>
                  </div>
                  <p className="text-sm text-zinc-300 italic">{result.meta_description}</p>
                </div>
              )}
              {result.intro && (
                <div>
                  <Label className="font-mono-tech text-[10px] text-zinc-500 mb-1.5 block">INTRO</Label>
                  <p className="text-zinc-200 leading-relaxed">{result.intro}</p>
                </div>
              )}
              {Array.isArray(result.sections) && result.sections.map((s, i) => (
                <div key={i}>
                  <h3 className="font-heading text-xl font-bold tracking-tight mb-2">{s.heading}</h3>
                  <p className="text-zinc-200 leading-relaxed whitespace-pre-wrap">{s.body}</p>
                </div>
              ))}
              {result.conclusion && (
                <div>
                  <Label className="font-mono-tech text-[10px] text-zinc-500 mb-1.5 block">CONCLUSIE</Label>
                  <p className="text-zinc-200 leading-relaxed">{result.conclusion}</p>
                </div>
              )}
              {Array.isArray(result.tags) && result.tags.length > 0 && (
                <div>
                  <Label className="font-mono-tech text-[10px] text-zinc-500 mb-2 block">TAGS</Label>
                  <div className="flex flex-wrap gap-2">
                    {result.tags.map((t, i) => (
                      <span key={i} className="text-xs bg-white/5 text-zinc-300 px-2.5 py-1 rounded-full">
                        #{t}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="border border-dashed border-white/10 rounded-lg p-12 text-center">
              <BookOpen className="w-10 h-10 text-amber-400 mx-auto mb-4" strokeWidth={1.5} />
              <div className="text-sm text-zinc-400">Je blogartikel verschijnt hier.</div>
            </div>
          )}
        </div>
      </div>

      {/* Schedule dialog */}
      <ScheduleDialog
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        content={result ? {
          title: result.title || "",
          body: [
            result.intro || "",
            ...(Array.isArray(result.sections) ? result.sections.map((s) => `## ${s.heading}\n\n${s.body}`) : []),
            result.conclusion || "",
          ].filter(Boolean).join("\n\n"),
          hashtags: result.tags || [],
          platform: "blog",
          sourceLanguage: language,
          contentType: "blog",
        } : null}
      />
    </div>
  );
}
